<template>
  <v-dialog
    v-if="featureEnabled"
    :value="open"
    persistent
    width="480"
    @keydown.esc="handleCancel"
  >
    <v-card class="ajfd-card pa-6 background-pattern-icon" data-testid="add-job-family-dialog">
      <div class="d-flex align-center mb-4">
        <span class="ajfd-featured-icon" data-testid="ajfd-featured-icon">
          <svg
            class="ajfd-layers-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="m22 12.7-8.4 3.82a2 2 0 0 1-1.6 0L3.6 12.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="m22 17.7-8.4 3.82a2 2 0 0 1-1.6 0L3.6 17.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <v-spacer />
        <button
          type="button"
          class="ajfd-close"
          aria-label="Close Add Job Family dialog"
          data-testid="ajfd-close"
          @click="handleCancel"
        >
          <svg
            class="ajfd-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <h2 id="ajfd-title" class="ajfd-title" data-testid="ajfd-title">Add Job Family</h2>

      <div class="ajfd-field">
        <label class="ajfd-label" for="ajfd-name-input" data-testid="ajfd-name-label">
          Name
          <span class="ajfd-required" aria-hidden="true">*</span>
        </label>
        <input
          id="ajfd-name-input"
          v-model.trim="name"
          class="ajfd-input"
          :class="{ 'ajfd-input--filled': hasName, 'ajfd-input--error': hasError('name') }"
          type="text"
          placeholder="e.g. Engineering"
          autocomplete="off"
          required
          :aria-required="true"
          :aria-invalid="hasError('name')"
          :aria-describedby="hasError('name') ? 'ajfd-name-error' : null"
          :disabled="busy"
          :maxlength="JOB_FAMILY_NAME_MAX_CHARACTERS"
          data-testid="ajfd-name-input"
        />
        <p
          v-if="hasError('name')"
          id="ajfd-name-error"
          class="ajfd-error"
          data-testid="ajfd-name-error"
        >
          {{ errors.name }}
        </p>
      </div>

      <p class="ajfd-helper" data-testid="ajfd-helper">
        New Job Families start in Draft status with a first level named "No career ladder." Add
        more levels afterwards from the Job Family detail view — each new level stacks on top and
        is numbered automatically.
      </p>

      <div class="ajfd-footer">
        <button
          type="button"
          class="ajfd-btn ajfd-btn--secondary"
          data-testid="ajfd-cancel"
          :disabled="busy"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ajfd-btn ajfd-btn--primary"
          :class="{ 'ajfd-btn--disabled': saveDisabled }"
          :disabled="saveDisabled"
          :aria-busy="busy ? 'true' : 'false'"
          data-testid="ajfd-save"
          @click="handleSave"
        >
          <span v-if="busy" class="ajfd-spinner" aria-hidden="true" data-testid="ajfd-save-spinner" />
          <span>{{ busy ? "Saving" : "Save" }}</span>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddJobFamilyDialog",
  // `open` is a v-model target (`v-model:open` at the call site), Vue 2.7's
  // multi-model syntax — matches the update:open convention used by
  // AIDataDialog / GeneratingWithAIDialog (`update:show`) in this codebase.
  model: {
    prop: "open",
    event: "update:open",
  },
};
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import { JOB_FAMILY_NAME_MAX_CHARACTERS } from "@components/constants/jobFamily.js";

/**
 * ER-11946 — AddJobFamilyDialog (Organization > Job Families "Add Job Family" action).
 *
 * Collapsed back to a single required Name field. The first Level's Name + Descriptor
 * fields (added under ER-11646) are removed — the backend now auto-creates the first
 * Level with a fixed name/descriptor (see ER-11945, backend). A Job Family must always
 * have at least one Level (Job Family rules page §3.3), so the atomic create contract
 * is unchanged — creating a family still creates its first level in the same operation,
 * but the admin no longer authors that first level's content in this dialog.
 *
 * Self-contained: the component owns its own Name text state (no v-model from the
 * parent). The parent owns only `open` (v-model:open), `busy` (disables the form and
 * shows the pending Save state while the create request is in flight) and `errors`
 * (server-side field errors keyed by `name`). Save stays disabled until Name is filled.
 * Submitting emits `create` with `{ name }` only — no nested level object. On a
 * validation failure the parent re-opens the dialog with `errors` populated; the
 * component never clears the entered value itself, so a partial save is never shown
 * as success. Cancel, the close X and Escape emit `cancel` (and `update:open`, false)
 * without emitting `create`.
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions") — unchanged from ER-11420, the same kill
 * switch that gates the JobFamiliesList table view.
 */
const props = defineProps({
  /** Boolean that controls dialog visibility (v-model:open). */
  open: {
    type: Boolean,
    default: false,
  },
  /** Disables the form and shows the pending Save state while create is in flight. */
  busy: {
    type: Boolean,
    default: false,
  },
  /** Server-side field errors: { name }. */
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:open", "create", "cancel"]);

const featureEnabled = ref(false);

const proxy = getCurrentInstance()?.proxy;

const name = ref("");

const hasName = computed(() => name.value.trim().length > 0);

const saveDisabled = computed(() => props.busy || !hasName.value);

const hasError = (field) => Boolean(props.errors && props.errors[field]);

function resetFields() {
  name.value = "";
}

// Values are preserved on validation failure (the parent keeps `open` true and
// sets `errors`); only reset the form once the dialog actually closes so the
// next Add starts blank.
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      resetFields();
    }
  }
);

function handleSave() {
  if (saveDisabled.value) {
    return;
  }
  emit("create", {
    name: name.value.trim(),
  });
}

function handleCancel() {
  emit("cancel");
  emit("update:open", false);
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.ajfd-card {
  background-color: #ffffff;
  border: 1px solid #e4e7ec;
  /* Vuetify's v-card radius must be flattened so the Untitled UI card keeps its shape. */
  border-radius: 16px !important;
}

.ajfd-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #f3edff;
  color: #7f56d9;
}

.ajfd-layers-icon {
  display: block;
}

.ajfd-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.ajfd-close:hover {
  background: #f2f4f7;
}

.ajfd-close:focus-visible,
.ajfd-input:focus-visible,
.ajfd-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.ajfd-title {
  margin: 0 0 20px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.ajfd-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
}

.ajfd-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.ajfd-required {
  color: #f04438;
}

.ajfd-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ajfd-input::placeholder {
  color: #667085;
}

.ajfd-input--filled {
  border-color: #7f56d9;
}

.ajfd-input--error {
  border-color: #fda29b;
}

.ajfd-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #f04438;
}

.ajfd-helper {
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 18px;
  color: #667085;
}

.ajfd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #eaecf0;
}

.ajfd-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 64px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.ajfd-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ajfd-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

/* Save (disabled/empty) state per Figma State=empty: #F2F4F7 background, no border, #98A2B3 text. */
.ajfd-btn--disabled,
.ajfd-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #98a2b3;
  box-shadow: none;
  cursor: not-allowed;
}

.ajfd-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ajfd-spin 0.6s linear infinite;
}

@keyframes ajfd-spin {
  to {
    transform: rotate(360deg);
  }
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
}
</style>
